import { useState, useEffect } from 'react'
import { useAuthStore } from '../../core/stores/authStore'
import { AlertTriangle, CalendarClock, Bell, FileText, Hammer, FolderOpen, TrendingUp, BarChart3, Loader2 } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import api from '../../core/services/api'
import { ScadenzaInScadenza } from '../../types/strumenti'
import { ScadenzaCanone } from '../../types/scadenziario'
import type { Notifica } from '../../types/notifiche'
import AgentInstallBanner from '../../components/common/AgentInstallBanner'
import GanttDashboardSection from '../../components/commesse/GanttDashboardSection'
import ResourceLoadSection from '../../components/commesse/ResourceLoadSection'

interface DocRecente {
  tipo: 'offerta' | 'commessa' | 'fascicolo'
  id: string
  label: string
  titolo: string
  updated_at: string | null
  link: string
}

interface OffertaPerStato {
  bozza: number
  in_revisione: number
  approvata: number
  in_attesa: number
  confermata: number
  rifiutata: number
}

interface ForecastMese extends OffertaPerStato {
  mese: string
}

interface CommessaAttiva {
  id: string
  numero_commessa: string
  titolo: string
  cliente_nome: string
  giorni_previsti: number
  giorni_svolti: number
  data_prevista_consegna: string | null
  in_ritardo: boolean
  giorni_sforati: number
}

interface DashboardStats {
  user_display_name: string
  documenti_recenti: DocRecente[]
  offerte_anno_corrente: OffertaPerStato
  offerte_anno_precedente: OffertaPerStato
  forecast_mesi: ForecastMese[]
  commesse_attive: CommessaAttiva[]
}

const STATO_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  bozza: { label: 'Bozza', color: 'bg-gray-400', bg: 'bg-gray-100' },
  in_revisione: { label: 'In Revisione', color: 'bg-yellow-500', bg: 'bg-yellow-50' },
  approvata: { label: 'Approvata', color: 'bg-blue-500', bg: 'bg-blue-50' },
  in_attesa: { label: 'In Attesa', color: 'bg-orange-500', bg: 'bg-orange-50' },
  confermata: { label: 'Confermata', color: 'bg-green-500', bg: 'bg-green-50' },
  rifiutata: { label: 'Rifiutata', color: 'bg-red-500', bg: 'bg-red-50' },
}

const DOC_ICONS = {
  offerta: FileText,
  commessa: Hammer,
  fascicolo: FolderOpen,
}

export default function DashboardPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()

  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loadingStats, setLoadingStats] = useState(true)
  const [scadenzeInArrivo, setScadenzeInArrivo] = useState<ScadenzaInScadenza[]>([])
  const [loadingScadenze, setLoadingScadenze] = useState(true)
  const [scadenzeCanoni, setScadenzeCanoni] = useState<ScadenzaCanone[]>([])
  const [loadingCanoni, setLoadingCanoni] = useState(true)
  const [notifiche, setNotifiche] = useState<Notifica[]>([])
  const [loadingNotifiche, setLoadingNotifiche] = useState(true)

  useEffect(() => {
    loadStats()
    loadScadenze()
    loadScadenzeCanoni()
    loadNotifiche()
  }, [])

  const loadStats = async () => {
    try {
      setLoadingStats(true)
      const res = await api.get('/v1/dashboard/stats')
      setStats(res.data)
    } catch {
      // silent
    } finally {
      setLoadingStats(false)
    }
  }

  const loadScadenze = async () => {
    try {
      setLoadingScadenze(true)
      const res = await api.get('/v1/strumenti/scadenze-in-scadenza')
      setScadenzeInArrivo(res.data)
    } catch {
      // silent
    } finally {
      setLoadingScadenze(false)
    }
  }

  const loadScadenzeCanoni = async () => {
    try {
      setLoadingCanoni(true)
      const res = await api.get('/v1/scadenziario/prossime')
      setScadenzeCanoni(res.data)
    } catch {
      // silent
    } finally {
      setLoadingCanoni(false)
    }
  }

  const loadNotifiche = async () => {
    try {
      setLoadingNotifiche(true)
      const res = await api.get('/v1/notifiche?solo_non_lette=false&limit=5')
      setNotifiche(res.data)
    } catch {
      // silent
    } finally {
      setLoadingNotifiche(false)
    }
  }

  const handleNotificaClick = async (n: Notifica) => {
    if (!n.letta) {
      try {
        await api.put(`/v1/notifiche/${n.id}/letta`)
        setNotifiche(prev => prev.map(x => x.id === n.id ? { ...x, letta: true } : x))
      } catch { /* silent */ }
    }
    if (n.link_url) navigate(n.link_url)
  }

  const formatTimeAgo = (iso: string) => {
    const d = new Date(iso)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    if (diffMin < 60) return `${diffMin}m fa`
    const diffH = Math.floor(diffMin / 60)
    if (diffH < 24) return `${diffH}h fa`
    const diffD = Math.floor(diffH / 24)
    if (diffD < 7) return `${diffD}g fa`
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    })
  }

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val)
  }

  const formatMese = (meseStr: string) => {
    const [y, m] = meseStr.split('-')
    const mesi = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
    return `${mesi[parseInt(m) - 1]} ${y}`
  }

  const statoTotal = (data: OffertaPerStato) =>
    data.bozza + data.in_revisione + data.approvata + data.in_attesa + data.confermata + data.rifiutata

  const hasOfferte = stats && (statoTotal(stats.offerte_anno_corrente) > 0 || statoTotal(stats.offerte_anno_precedente) > 0)
  const hasForecast = stats && stats.forecast_mesi.some(m => statoTotal(m) > 0)

  // ── Offerte bar chart renderer ──
  const renderStatoBars = (data: OffertaPerStato) => {
    const total = statoTotal(data)
    if (total === 0) return <p className="text-sm text-gray-400 italic">Nessuna offerta</p>

    const entries = Object.entries(STATO_CONFIG)
      .map(([key, cfg]) => ({ key, ...cfg, value: data[key as keyof OffertaPerStato] as number }))
      .filter(e => e.value > 0)

    return (
      <div className="space-y-2">
        {entries.map(e => (
          <div key={e.key} className="flex items-center gap-2">
            <span className="text-xs text-gray-500 w-24 text-right truncate">{e.label}</span>
            <div className="flex-1 h-5 bg-gray-100 rounded overflow-hidden">
              <div
                className={`h-full ${e.color} rounded transition-all`}
                style={{ width: `${Math.max(2, (e.value / total) * 100)}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-700 w-20 text-right">{formatCurrency(e.value)}</span>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1 border-t border-gray-200">
          <span className="text-xs font-semibold text-gray-700 w-24 text-right">Totale</span>
          <div className="flex-1" />
          <span className="text-sm font-bold text-gray-900 w-20 text-right">{formatCurrency(total)}</span>
        </div>
      </div>
    )
  }

  const displayName = stats?.user_display_name || user?.username || ''

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          Benvenuto, {displayName}
        </h1>
        <p className="text-gray-500 mt-1">
          {user?.tenant_name || 'Dashboard aziendale'}
        </p>
      </div>

      <AgentInstallBanner />

      {loadingStats ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <>
          {/* Row 1: Documenti Recenti + Notifiche */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ultimi Documenti */}
            {stats && stats.documenti_recenti.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary-500" />
                  Ultimi documenti
                </h2>
                <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
                  {stats.documenti_recenti.map(doc => {
                    const Icon = DOC_ICONS[doc.tipo]
                    return (
                      <Link
                        key={`${doc.tipo}-${doc.id}`}
                        to={doc.link}
                        className="flex items-center gap-3 p-3.5 hover:bg-gray-50 transition-colors"
                      >
                        <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{doc.label}</span>
                            <span className="text-sm text-gray-700 truncate">{doc.titolo}</span>
                          </div>
                        </div>
                        {doc.updated_at && (
                          <span className="text-xs text-gray-400 flex-shrink-0">{formatTimeAgo(doc.updated_at)}</span>
                        )}
                      </Link>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Notifiche */}
            {!loadingNotifiche && notifiche.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="w-5 h-5 text-blue-500" />
                  Le tue assegnazioni recenti
                </h2>
                <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
                  {notifiche.map(n => (
                    <button
                      key={n.id}
                      onClick={() => handleNotificaClick(n)}
                      className="w-full text-left p-3.5 flex items-center gap-3 hover:bg-gray-50 transition-colors"
                      style={{ opacity: n.letta ? 0.6 : 1 }}
                    >
                      {!n.letta && (
                        <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">{n.titolo}</p>
                        {n.messaggio && (
                          <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{n.messaggio}</p>
                        )}
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{formatTimeAgo(n.created_at)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Row 2: Offerte Anno Corrente / Precedente */}
          {hasOfferte && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Offerte per anno
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {new Date().getFullYear()}
                  </h3>
                  {renderStatoBars(stats!.offerte_anno_corrente)}
                </div>
                <div className="bg-white rounded-xl shadow-sm p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">
                    {new Date().getFullYear() - 1}
                  </h3>
                  {renderStatoBars(stats!.offerte_anno_precedente)}
                </div>
              </div>
            </div>
          )}

          {/* Row 3: Forecast Mensile */}
          {hasForecast && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-violet-500" />
                Forecast mensile
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {stats!.forecast_mesi.map(mese => (
                  <div key={mese.mese} className="bg-white rounded-xl shadow-sm p-5">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      {formatMese(mese.mese)}
                    </h3>
                    {renderStatoBars(mese)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Row 4: Commesse Attive */}
          {stats && stats.commesse_attive.length > 0 && (
            <div className="mt-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Hammer className="w-5 h-5 text-amber-500" />
                Commesse attive
              </h2>
              <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
                {stats.commesse_attive.map(c => {
                  const pct = c.giorni_previsti > 0
                    ? Math.min(100, Math.round((c.giorni_svolti / c.giorni_previsti) * 100))
                    : 0
                  const isOverBudget = c.giorni_svolti > c.giorni_previsti && c.giorni_previsti > 0
                  const isLate = c.in_ritardo
                  const danger = isOverBudget || isLate

                  return (
                    <Link
                      key={c.id}
                      to={`/app/commesse/${c.id}`}
                      className="block p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">{c.numero_commessa}</span>
                            <span className="text-sm font-medium text-gray-900 truncate">{c.titolo}</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">{c.cliente_nome}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-4">
                          <p className={`text-sm font-medium ${danger ? 'text-red-600' : 'text-gray-600'}`}>
                            {c.giorni_svolti}/{c.giorni_previsti} gg
                            {isOverBudget && ` (+${c.giorni_sforati})`}
                          </p>
                          {c.data_prevista_consegna && (
                            <p className={`text-xs ${isLate ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                              {isLate ? 'IN RITARDO - ' : 'Consegna '}
                              {formatDate(c.data_prevista_consegna)}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            danger ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(100, pct)}%` }}
                        />
                      </div>
                    </Link>
                  )
                })}
              </div>
            </div>
          )}

          {/* Row 5: Gantt Commesse */}
          <div className="mt-8">
            <GanttDashboardSection />
          </div>

          {/* Row 6: Carico Risorse */}
          <div className="mt-8">
            <ResourceLoadSection />
          </div>

          {/* Row 7: Scadenze (side by side) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
            {/* Scadenze Strumenti */}
            {!loadingScadenze && scadenzeInArrivo.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Scadenze Strumenti
                </h2>
                <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
                  {scadenzeInArrivo.map(s => (
                    <div key={s.id} className="p-4 flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm">{s.descrizione}</p>
                        <p className="text-xs text-gray-500 truncate">{s.strumento_descrizione}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className={`text-xs font-medium ${
                          s.giorni_rimanenti <= 0 ? 'text-red-600' :
                          s.giorni_rimanenti <= 7 ? 'text-amber-600' : 'text-gray-600'
                        }`}>
                          {s.giorni_rimanenti <= 0 ? 'SCADUTO' :
                           s.giorni_rimanenti === 1 ? 'Scade domani' :
                           `Tra ${s.giorni_rimanenti}gg`}
                        </p>
                        <p className="text-xs text-gray-400">{formatDate(s.data_scadenza)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Scadenze Canoni */}
            {!loadingCanoni && scadenzeCanoni.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CalendarClock className="w-5 h-5 text-indigo-500" />
                    Scadenze Canoni
                  </h2>
                  <Link
                    to="/app/scadenziario"
                    className="text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    Vedi tutte
                  </Link>
                </div>
                <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
                  {scadenzeCanoni.map(s => (
                    <div key={s.id} className="p-4 flex items-center justify-between">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 text-sm">{s.descrizione_canone}</p>
                        <p className="text-xs text-gray-500">
                          {s.cliente_nome} &mdash; {s.numero_offerta}
                          {s.frequenza_label && <span className="text-indigo-500 ml-2">({s.frequenza_label})</span>}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className={`text-xs font-medium ${
                          s.giorni_rimanenti <= 0 ? 'text-red-600' :
                          s.giorni_rimanenti <= 30 ? 'text-amber-600' : 'text-gray-600'
                        }`}>
                          {s.giorni_rimanenti <= 0 ? 'SCADUTO' :
                           s.giorni_rimanenti === 1 ? 'Scade domani' :
                           `Tra ${s.giorni_rimanenti}gg`}
                        </p>
                        <p className="text-xs text-gray-400">{formatDate(s.data_scadenza)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
